<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Users</span>
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Users</h1>
        <p class="page-head__subtitle">
          Everyone with a CapX account, the roles they hold, and their authority tier. Add staff
          members and manage their access here.
        </p>
      </div>

      <div class="page-head__actions">
        <button mat-flat-button color="primary" type="button" (click)="toggleAdd()">
          <app-icon name="plus" [size]="18" /> Add staff member
        </button>
      </div>
    </div>
  </header>

  <!-- ─── Add staff member ─────────────────────────────────────────────── -->
  @if (addOpen()) {
    <section class="users-add" aria-label="Add staff member">
      <form [formGroup]="createForm" (ngSubmit)="submitCreate()" class="users-add__form">
        <div class="users-add__grid">
          <mat-form-field appearance="outline">
            <mat-label>First name</mat-label>
            <input matInput formControlName="first_name" autocomplete="off" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Last name</mat-label>
            <input matInput formControlName="last_name" autocomplete="off" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Email</mat-label>
            <input matInput type="email" formControlName="email" autocomplete="off" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Authority tier</mat-label>
            <mat-select formControlName="ops_tier">
              <mat-option [value]="null">No tier (non-Ops)</mat-option>
              @for (t of tierOptions; track t.value) {
                <mat-option [value]="t.value">{{ t.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="users-add__roles">
            <mat-label>Roles</mat-label>
            <mat-select formControlName="role_slugs" multiple>
              @for (r of roles(); track r.slug) {
                <mat-option [value]="r.slug">{{ r.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <p class="users-add__help">
          <strong>Roles</strong> decide which areas the account can open (back office, Ops queues,
          agent portal). The <strong>authority tier</strong> sets how far up the Ops sign-off ladder
          the user can go — it only applies once they hold the CapX Ops role.
        </p>

        @if (createTierWithoutOps()) {
          <p class="users-add__warn">
            <app-icon name="alert-triangle" [size]="14" aria-hidden="true" />
            The chosen tier has no effect until you also add the <strong>CapX Ops</strong> role.
          </p>
        }

        <mat-checkbox formControlName="is_credit_committee_member">
          Credit Committee member
        </mat-checkbox>

        <mat-checkbox formControlName="notify_new_applications">
          Notify of new applications
        </mat-checkbox>

        <p class="users-add__note">
          The new staff member is emailed a link to set their password. No password is set here.
        </p>

        <div class="users-add__actions">
          <button mat-button type="button" (click)="toggleAdd()" [disabled]="creating()">
            Cancel
          </button>
          <button mat-flat-button color="primary" type="submit" [disabled]="creating()">
            {{ creating() ? 'Sending invite…' : 'Create & send invite' }}
          </button>
        </div>
      </form>
    </section>
  }

  <!-- ─── Search & filters ─────────────────────────────────────────────── -->
  <div class="users-toolbar">
    <mat-form-field appearance="outline" class="users-toolbar__search">
      <mat-label>Search users</mat-label>
      <app-icon name="search" [size]="18" matPrefix aria-hidden="true" />
      <input
        matInput
        type="search"
        [value]="searchTerm()"
        (change)="onSearch($any($event.target).value)"
        placeholder="Name or email"
      />
    </mat-form-field>

    <div class="users-toolbar__filters">
      <mat-button-toggle-group
        class="users-toolbar__toggle"
        [value]="statusFilter()"
        (change)="setStatusFilter($event.value)"
        aria-label="Filter by status"
      >
        <mat-button-toggle value="all">All</mat-button-toggle>
        <mat-button-toggle value="active">Active</mat-button-toggle>
        <mat-button-toggle value="inactive">Inactive</mat-button-toggle>
        <mat-button-toggle value="unverified">Unverified</mat-button-toggle>
      </mat-button-toggle-group>

      <mat-button-toggle-group
        class="users-toolbar__toggle"
        [value]="accessFilter()"
        (change)="setAccessFilter($event.value)"
        aria-label="Filter by access"
      >
        <mat-button-toggle value="all">Anyone</mat-button-toggle>
        <mat-button-toggle value="ops">Ops</mat-button-toggle>
        <mat-button-toggle value="mcp">MCP</mat-button-toggle>
        <mat-button-toggle value="admin">Admin</mat-button-toggle>
      </mat-button-toggle-group>

      @if (filtersActive()) {
        <button mat-button type="button" (click)="resetFilters()">
          Clear filters
        </button>
      }
    </div>
  </div>

  <!-- ─── States ───────────────────────────────────────────────────────── -->
  @if (loading()) {
    <div class="users-table-wrap" aria-label="Loading users" aria-busy="true">
      <table class="users-table">
        <thead>
          <tr>
            @for (c of [1, 2, 3, 4, 5]; track c) {
              <th scope="col"><span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span></th>
            }
          </tr>
        </thead>
        <tbody>
          @for (r of [1, 2, 3, 4, 5, 6, 7, 8]; track r) {
            <tr>
              @for (c of [1, 2, 3, 4, 5]; track c) {
                <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span></td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
  } @else if (error()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load users</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <button mat-stroked-button type="button" (click)="load()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </div>
  } @else if (users().length === 0) {
    @if (hasSearch()) {
      <div class="sg-empty sg-empty--bordered">
        <app-icon name="search" [size]="40" />
        <p class="sg-empty__title">No results</p>
        <p class="sg-empty__desc">No users match "{{ searchTerm() }}".</p>
        <button mat-stroked-button type="button" (click)="clearSearch()">Clear search</button>
      </div>
    } @else {
      <div class="sg-empty sg-empty--bordered">
        <app-icon name="users" [size]="40" />
        <p class="sg-empty__title">No users yet</p>
        <p class="sg-empty__desc">Add your first staff member to get started.</p>
        <button mat-flat-button color="primary" type="button" (click)="toggleAdd()">
          <app-icon name="plus" [size]="18" /> Add staff member
        </button>
      </div>
    }
  } @else if (visibleUsers().length === 0) {
    <div class="sg-empty sg-empty--bordered">
      <app-icon name="search" [size]="40" />
      <p class="sg-empty__title">No matches</p>
      <p class="sg-empty__desc">No users match the current filters.</p>
      <button mat-stroked-button type="button" (click)="resetFilters()">
        Clear filters
      </button>
    </div>
  } @else {
    <div class="users-table-wrap">
      <table class="users-table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Status</th>
            <th scope="col">Roles</th>
            <th scope="col">Authority</th>
            <th scope="col">MCP</th>
            <th scope="col" class="users-table__last">Last sign-in</th>
            <th scope="col" aria-label="Manage"></th>
          </tr>
        </thead>
        <tbody>
          @for (u of visibleUsers(); track u.id) {
            <tr
              class="users-row"
              [class.users-row--open]="expandedId() === u.id"
              [class.users-row--inactive]="!u.is_active"
            >
              <td>
                <div class="users-name">{{ u.full_name | dash }}</div>
                <div class="users-email">{{ u.email }}</div>
              </td>
              <td>
                <span class="users-badge" [attr.data-variant]="u.is_active ? 'success' : 'neutral'">
                  {{ u.is_active ? 'Active' : 'Inactive' }}
                </span>
                @if (!u.email_verified_at) {
                  <span class="users-badge" data-variant="warning">Unverified</span>
                }
              </td>
              <td>
                @if (u.system_roles.length) {
                  <span class="users-chips">
                    @for (r of u.system_roles; track r.slug) {
                      <span class="users-badge" data-variant="neutral">{{ r.name }}</span>
                    }
                  </span>
                } @else {
                  <span class="users-muted">—</span>
                }
              </td>
              <td>
                @if (u.ops_tier) {
                  <span class="users-badge" data-variant="default">{{ tierLabels[u.ops_tier] }}</span>
                } @else {
                  <span class="users-muted">—</span>
                }
                @if (u.is_credit_committee_member) {
                  <span class="users-badge" data-variant="neutral" title="Credit Committee member">CC</span>
                }
              </td>
              <td>
                @if (u.mcp_access_tier) {
                  <span
                    class="users-badge"
                    [attr.data-variant]="u.mcp_access_tier === 'HIGH' ? 'warning' : 'default'"
                    [title]="'MCP access: ' + u.mcp_access_label"
                  >
                    {{ mcpLabels[u.mcp_access_tier] }}
                  </span>
                } @else {
                  <span class="users-muted">—</span>
                }
              </td>
              <td class="users-table__last">{{ (u.last_login | date: 'medium') | dash }}</td>
              <td class="users-table__manage">
                <button
                  mat-stroked-button
                  type="button"
                  (click)="toggleRow(u.id)"
                  [attr.aria-expanded]="expandedId() === u.id"
                >
                  {{ expandedId() === u.id ? 'Close' : 'Manage' }}
                </button>
              </td>
            </tr>

            @if (expandedId() === u.id) {
              <tr class="users-detail-row">
                <td colspan="7">
                  <div class="users-detail">
                    <div class="users-detail__col">
                      <h3 class="users-detail__heading">Roles</h3>
                      @if (roles().length) {
                        <div class="users-detail__roles">
                          @for (role of roles(); track role.slug) {
                            <mat-checkbox
                              [checked]="hasRole(u, role.slug)"
                              [disabled]="savingId() === u.id || roleLocked(u, role)"
                              (change)="toggleRole(u, role, $event.checked)"
                            >
                              {{ role.name }}
                              @if (roleLocked(u, role)) {
                                <span class="users-detail__hint">(your own admin role)</span>
                              }
                            </mat-checkbox>
                          }
                        </div>
                      } @else {
                        <p class="users-muted">No roles available.</p>
                      }
                      <p class="users-detail__help">
                        Roles decide which areas the account can open — back office, the Ops
                        queues, the agent portal.
                      </p>
                    </div>

                    <div class="users-detail__col">
                      <h3 class="users-detail__heading">Authority tier</h3>
                      <mat-form-field appearance="outline" class="users-detail__tier">
                        <mat-label>Tier</mat-label>
                        <mat-select
                          [value]="u.ops_tier"
                          [disabled]="savingId() === u.id"
                          (selectionChange)="setOpsTier(u, $event.value)"
                        >
                          <mat-option [value]="null">No tier (non-Ops)</mat-option>
                          @for (t of tierOptions; track t.value) {
                            <mat-option [value]="t.value">{{ t.label }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>

                      <p class="users-detail__help">
                        The tier sets how far up the Ops sign-off ladder they can go (e.g.
                        approving larger facilities).
                      </p>

                      @if (u.ops_tier && !hasOpsRole(u)) {
                        <p class="users-detail__warn">
                          <app-icon name="alert-triangle" [size]="14" aria-hidden="true" />
                          This tier has no effect until the user also holds the
                          <strong>CapX Ops</strong> role.
                        </p>
                      }

                      <mat-checkbox
                        [checked]="u.is_credit_committee_member"
                        [disabled]="savingId() === u.id"
                        (change)="setCcMember(u, $event.checked)"
                      >
                        Credit Committee member
                      </mat-checkbox>

                      <mat-checkbox
                        [checked]="u.notify_new_applications"
                        [disabled]="savingId() === u.id"
                        (change)="setNotifyNewApplications(u, $event.checked)"
                        [attr.data-testid]="'notify-new-applications-' + u.id"
                      >
                        Notify of new applications
                      </mat-checkbox>
                    </div>

                    <div class="users-detail__col">
                      <h3 class="users-detail__heading">Account</h3>
                      <mat-slide-toggle
                        [checked]="u.is_active"
                        [disabled]="savingId() === u.id || u.id === currentUserId"
                        (change)="setActive(u, $event.checked)"
                      >
                        Active
                      </mat-slide-toggle>
                      @if (u.id === currentUserId) {
                        <p class="users-detail__hint">You can't deactivate your own account.</p>
                      }

                      <h3 class="users-detail__heading users-detail__heading--spaced">MCP access</h3>
                      <div class="users-detail__mcp">
                        @if (u.mcp_access_tier) {
                          <span
                            class="users-badge"
                            [attr.data-variant]="u.mcp_access_tier === 'HIGH' ? 'warning' : 'default'"
                          >
                            {{ u.mcp_access_label }}
                          </span>
                        } @else {
                          <span class="users-muted">No access</span>
                        }
                        <button
                          mat-stroked-button
                          type="button"
                          (click)="openMcpAccess(u)"
                        >
                          <app-icon name="shield" [size]="16" /> Manage MCP
                        </button>
                      </div>
                      <p class="users-detail__help">
                        Lets the user connect Claude to the CapX MCP server. Read-only;
                        granted explicitly per person.
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  }
</div>
